'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, Stack, IconButton, Select, MenuItem, FormControl, InputLabel, Button } from '@mui/material';
import CalendarDay from './calendar_day_item';
import { getVenueOpenHours } from '../../lib/availability_calculator';
import { Venue, VenueBookedTimeSlot } from '../../../../shared/models';
import { getVenueBookedTimeSlotsByDay } from '../../lib/firebase/bookings';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { TimeRange } from '../../../../shared/types';
import LoadingLogo from '../loading_logo';
interface CustomCalendarProps {
    venue: Venue;
    selectedDate: Date;
    minDate?: Date;
    maxDate?: Date;
    selectedSection?: string;
    onSectionChange: (sectionId: string) => void;
    onDateChange: (date: Date, selectedSection: string, bookedTimeSlots: Record<string, VenueBookedTimeSlot[]>) => void;
}

const CustomCalendar = ({
    venue,
    selectedDate,
    minDate,
    maxDate,
    selectedSection: initialSelectedSection,
    onDateChange,
    onSectionChange
}: CustomCalendarProps) => {
    const [currentMonth, setCurrentMonth] = useState(() => new Date(selectedDate));
    const [venueBookedTimeSlots, setVenueBookedTimeSlots] = useState<Record<string, VenueBookedTimeSlot[]>>({});
    const [selectedSection, setSelectedSection] = useState(() => {
        const sections = venue?.Sections ? Object.keys(venue.Sections) : [];
        return initialSelectedSection || sections[0] || '';
    });


    const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    const sectionColors = ['#E0B1EF', '#4CAF50', '#2196F3', '#FFC107', '#4C5B5C', '#3454D1', '#34D1BF', '#1B998B'];

    const sectionColorMap = useMemo(() => {
        return Object.fromEntries(
            Object.keys(venue.Sections)
                .sort()
                .map((id, i) => [id, sectionColors[i % sectionColors.length]])
        );
    }, [venue.Sections]);

    const calendarDays = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1, 0, 0, 0, 0);
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const startDay = firstDay.getDay();

        return [
            // Fix for previous month's days
            ...Array(startDay).fill(null).map((_, i) => {
                const date = new Date(year, month, -startDay + i + 1);
                date.setHours(0, 0, 0, 0);
                return date;
            }),
            // Current month days (unchanged)
            ...Array(daysInMonth).fill(null).map((_, i) => {
                const date = new Date(year, month, i + 1);
                date.setHours(0, 0, 0, 0);
                return date;
            }),
            // Next month days (unchanged)
            ...Array(42 - (startDay + daysInMonth)).fill(null).map((_, i) => {
                const date = new Date(year, month + 1, i + 1);
                date.setHours(0, 0, 0, 0);
                return date;
            })
        ];
    }, [currentMonth]);

    useEffect(() => {
        const fetchBookedTimeSlots = async () => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Calculate the first day we display from the previous month
            const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
            const daysFromPrevMonth = firstDayOfMonth.getDay(); // 0-6 days from previous month
            const firstVisibleDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), -daysFromPrevMonth + 1);
            firstVisibleDay.setHours(0, 0, 0, 0);

            // Calculate the last day we display from the next month
            const daysInCurrentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
            const lastDayOfCurrentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), daysInCurrentMonth);
            const totalDaysInCalendarView = 42; // 6 rows of 7 days
            const daysFromNextMonth = totalDaysInCalendarView - daysFromPrevMonth - daysInCurrentMonth;

            // Calculate the actual last visible day in the calendar
            const lastVisibleDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, daysFromNextMonth);
            lastVisibleDay.setHours(23, 59, 59, 999);

            if (lastVisibleDay >= today) {
                const firstDay = firstVisibleDay < today ? today : firstVisibleDay;
                const slots = await getVenueBookedTimeSlotsByDay(venue.ID, firstDay, lastVisibleDay);
                setVenueBookedTimeSlots(slots);
            } else {
                setVenueBookedTimeSlots({});
            }
        };
        fetchBookedTimeSlots();
    }, [currentMonth, venue.ID]);

    const isDateDisabled = useCallback((date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const compareDate = new Date(date);
        compareDate.setHours(0, 0, 0, 0);

        const isBeforeToday = compareDate < today;
        const isBeforeMinDate = minDate && compareDate < minDate;
        const isAfterMaxDate = maxDate && compareDate > maxDate;

        return isBeforeToday || isBeforeMinDate || isAfterMaxDate;
    }, [minDate, maxDate]);

    const getAvailabilityForDate = useCallback((date: Date) => {
        // Initialize today's date at midnight
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Initialize compare date at midnight
        const compareDate = new Date(date);
        compareDate.setHours(0, 0, 0, 0);

        // Return empty time ranges if date is in the past
        if (compareDate < today) {
            const emptyTimeRanges = {
                id: 'venue',
                timeRanges: []
            };
            return [emptyTimeRanges];
        }

        // Set up start and end dates for the given date
        const startDate = new Date(date);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
        endDate.setMilliseconds(endDate.getMilliseconds() - 1);

        // Get venue hours and format them
        const venueHours = getVenueOpenHours(venue, startDate, endDate);
        const formattedTimeRanges = venueHours.map(time => {
            const formattedStart = time.start.toLocaleTimeString('en-US', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit'
            });
            const formattedEnd = time.end.toLocaleTimeString('en-US', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit'
            });

            return {
                start: formattedStart,
                end: formattedEnd,
                isAvailable: true
            };
        });

        const availabilityResult = [{
            id: 'venue',
            timeRanges: formattedTimeRanges
        }];

        return availabilityResult;
    }, [venue]);

    const getBookedTimesForDate = useCallback((date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const compareDate = new Date(date);
        compareDate.setHours(0, 0, 0, 0);

        if (compareDate < today) {
            return [];
        }

        const dateKey = date.toDateString();
        const bookedTimes = venueBookedTimeSlots[dateKey] || [];

        if (date.toDateString() === new Date('2024-11-28T00:00:00').toDateString()) {
            console.log('bookedTimes', bookedTimes);
        }
        // Map booked times to formatted time ranges
        const formattedBookedTimes = bookedTimes.map(slot => {
            // Format start time
            const startTime = new Date(slot.StartTime).toLocaleTimeString('en-US', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit'
            });

            // Format end time 
            const endTime = new Date(slot.EndTime).toLocaleTimeString('en-US', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit'
            });

            // Return formatted booking
            return {
                id: slot.SectionID,
                timeRanges: [{
                    start: startTime,
                    end: endTime
                }]
            };
        });

        return formattedBookedTimes;
    }, [venueBookedTimeSlots]);

    const handleMonthChange = useCallback((increment: number) => {
        setCurrentMonth(prevMonth => {
            const newDate = new Date(prevMonth);
            newDate.setMonth(prevMonth.getMonth() + increment);
            newDate.setHours(0, 0, 0, 0);
            return newDate;
        });
    }, []);

    const handleSectionChange = useCallback((sectionId: string) => {
        setSelectedSection(sectionId);
        onSectionChange(sectionId);
    }, [onSectionChange]);

    const handleDateChange = (date: Date) => {
        onDateChange(date, selectedSection, venueBookedTimeSlots);
    };


    // Add a safety check for when venue or sections are not yet available
    if (!venue?.Sections || Object.keys(venue.Sections).length === 0 || !selectedSection) {
        return <LoadingLogo />
    }

    return (
        <Box sx={{
            width: { xs: '100%', sm: 'auto' },
            height: { xs: '100%', sm: 'auto' },
            bgcolor: '#1C1C34',
            p: { xs: 1, sm: 2 },
            boxShadow: { xs: 'none', sm: '0px 4px 16px rgba(0, 0, 0, 0.2)' },
            top: { xs: 0, sm: 'auto' },
            left: { xs: 0, sm: 'auto' },
            zIndex: 1300,
            maxWidth: '100%',
            overflowX: 'hidden'
        }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: 'auto',
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                }}>
                    <IconButton
                        onClick={() => handleMonthChange(-1)}
                        size="small"
                        sx={{ color: '#FFFFFF' }}
                    >
                        <ChevronLeft />
                    </IconButton>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 600,
                            color: '#FFFFFF',
                            mx: 1,
                            fontSize: '0.875rem',
                            fontFamily: 'Inter'
                        }}
                    >
                        {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </Typography>
                    <IconButton
                        onClick={() => handleMonthChange(1)}
                        size="small"
                        sx={{ color: '#FFFFFF' }}
                    >
                        <ChevronRight />
                    </IconButton>
                </Box>

                <FormControl
                    variant="outlined"
                    size="small"
                    sx={{
                        flex: 1,
                        width: { xs: '100%', sm: 'auto' }, // Full width on mobile
                        background: 'linear-gradient(45deg, rgba(240,209,255,0.1) 0%, rgba(255,255,255,0.2) 100%)',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': {
                                borderColor: 'rgba(240,209,255,0.4)',
                                borderWidth: '2px',
                            },
                            '&:hover fieldset': {
                                borderColor: '#F0D1FF',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#F0D1FF',
                                borderWidth: '2px',
                            }
                        },
                        '& .MuiInputLabel-root': {
                            color: '#F0D1FF',
                            fontWeight: 500,
                            '&.Mui-focused': {
                                color: '#F0D1FF'
                            }
                        },
                        '& .MuiSelect-icon': {
                            color: '#F0D1FF',
                        }
                    }}
                >
                    <InputLabel id="section-select-label">Section</InputLabel>
                    <Select
                        labelId="section-select-label"
                        value={selectedSection}
                        label="Choose a Section"
                        onChange={(e) => handleSectionChange(e.target.value)}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    bgcolor: '#1C1C34',
                                    border: '2px solid #F0D1FF',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                                    '& .MuiMenuItem-root': {
                                        color: 'white',
                                        '&:hover': {
                                            bgcolor: 'rgba(240,209,255,0.1)',
                                        },
                                        '&.Mui-selected': {
                                            bgcolor: 'rgba(240,209,255,0.2)',
                                            '&:hover': {
                                                bgcolor: 'rgba(240,209,255,0.3)',
                                            }
                                        }
                                    }
                                }
                            }
                        }}
                    >
                        {Object.entries(venue.Sections)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([id, section]) => (
                                <MenuItem key={id} value={id}>
                                    {section.Name}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)', width: '100%', // Ensure full width
            }}>
                {weekDays.map(day => (
                    <Typography
                        key={day}
                        variant="subtitle2"
                        align="center"
                        sx={{
                            fontWeight: 600,
                            color: '#FFFFFF',
                            mb: 1,
                            fontSize: { xs: '0.8rem', sm: 'inherit' },
                            fontFamily: 'Inter'
                        }}
                    >
                        {day}
                    </Typography>
                ))}

                {venue.Sections && venue.Sections[selectedSection] && calendarDays.map((date, i) => (
                    <CalendarDay
                        key={i}
                        date={date}
                        isDisabled={isDateDisabled(date)}
                        isSelected={date.toDateString() === selectedDate.toDateString()}
                        isCurrentMonth={date.getMonth() === currentMonth.getMonth()}
                        onClick={() => !isDateDisabled(date) && handleDateChange(date)}
                        section={selectedSection}
                        sectionAvailabilities={[{
                            id: selectedSection,
                            name: venue.Sections[selectedSection].Name,
                            color: sectionColorMap[selectedSection],
                            timeRanges: getAvailabilityForDate(date)[0].timeRanges
                        }]}
                        sectionBookedTimes={[
                            {
                                id: selectedSection,
                                timeRanges: getBookedTimesForDate(date)
                                    .filter(booking => booking.id === selectedSection)
                                    .flatMap(booking => booking.timeRanges)
                            },
                            ...getBookedTimesForDate(date)
                                .filter(booking => venue.Sections[booking.id]?.OverlapsAllOtherSections)
                                .map(booking => ({
                                    id: booking.id,
                                    timeRanges: booking.timeRanges
                                }))
                        ]}
                        venue={venue}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default CustomCalendar;
